import { FunctionComponent, useCallback } from "react";
import "./LandingPage.scss";
import { useNavigate, useLocation } from "react-router-dom";
import images from "../../assets/images";
import { getScreenDimensions } from "../../utils/helper";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { SupportAgent } from "@mui/icons-material";
import { logout } from "../../redux/slices/authSlice";
import { trackButtonClick } from "../../firebase/analyticsHelper";
import { ButtonClickEvents, ButtonClickEventType } from "../../firebase/constants/events";

// import VirtualSchoolLandingPage from "../VirtualSchoolLandingPage/VirtualSchoolLandingPage";

const SupportCard: FunctionComponent = () => (
  <div className="lpSupportGlassCard">
    <div className="supportIconWrapper">
      <SupportAgent className="supportAgentIcon" />
    </div>
    <div className="supportContent">
      <span className="supportCardTitle">Need Assistance? Our team is here to help</span>
      <div className="supportLinksRow">
        <div className="supportLinkItem">
          <span className="supportLinkDesc">Support: </span>
          <span className="supportCardNum">
            {process.env.REACT_APP_SUPPORT_PHONE || ""}
          </span>
        </div>
        <div className="supportLinkDivider">|</div>
        <div className="supportLinkItem">
          <span className="supportLinkDesc">Toll-Free: </span>
          <span className="supportCardNum">
            {process.env.REACT_APP_SUPPORT_TOLL_FREE || ""}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const LandingPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { landingPageImage, backArrow } = images;
  const { isMobileScreen } = getScreenDimensions();
  const { loginAs, isKaton } = useAppSelector((state) => state.auth);

  const isVirtualSchool =
    location.pathname.includes("/virtual-school") ||
    isKaton === "virtualSchool" ||
    location.search.includes("isKaton=virtualSchool");

  const loginButtonOrder: string[] = ["student", "teacher", "virtualschool", "pta", "schooladmin"];
  const loginText: Record<
    string,
    { label: string; link: string; eventName?: ButtonClickEventType; description?: string }
  > = {
    student: {
      label: "Student",
      link: "/student-signup",
      eventName: ButtonClickEvents?.JOIN_CLASSROOM_AS_STUDENT_CLICK,
      description: "User clicked Student button",
    },
    teacher: {
      label: "Teacher",
      link: "/teacher-login",
      eventName: ButtonClickEvents?.TEACHER_TEXT_CLICK,
      description: "User clicked Teacher button",
    },
    pta: {
      label: "PTA",
      link: "/pta-verifyCode",
      eventName: ButtonClickEvents?.PTA_TEXT_CLICK,
      description: "User clicked PTA button",
    },
    schooladmin: {
      label: "School Admin",
      link: "/admin-login",
      eventName: ButtonClickEvents?.SCHOOL_ADMIN_TEXT_CLICK,
      description: "User clicked School Admin button",
    },
    virtualschool: {
      label: "Virtual School",
      link: "/virtual-school",
      eventName: ButtonClickEvents?.VIRTUAL_SCHOOL_TEXT_CLICK,
      description: "User clicked Virtual School button",
    },
  };
  const displayButtons = loginButtonOrder?.filter((val: string) => val !== loginAs);

  const onButtonClick = useCallback(
    (
      link: string,
      eventName: ButtonClickEventType = ButtonClickEvents?.JOIN_CLASSROOM_AS_STUDENT_CLICK,
      description = "User clicked the Join Classroom as Student button"
    ) => {
      trackButtonClick(eventName, {
        description,
        page: "Landing Page",
      });
      navigate(link);
    },
    [navigate]
  );

  const onBackClick = useCallback(() => {
    trackButtonClick(ButtonClickEvents?.BACK_TO_LOGIN_BUTTON_CLICK, {
      description: "User clicked back button to return to main landing page",
      page: "Landing Page",
    });
    dispatch(logout());
    navigate("/");
  }, [dispatch, navigate]);

  const landingImage = () => (
    <div className="bnrImg">
      <img className="rightImgIcon" alt="bannerImage" src={landingPageImage} data-testid="rightSideImage" />
    </div>
  );

  // if (isVirtualSchool) {
  //   return <VirtualSchoolLandingPage />;
  // }

  return (
    <section className="sectionBanner">
      <div className="landingPagecontainer">
        <div className="leftSet">
          {isVirtualSchool ? (
            <>
              {isMobileScreen && (
                <div className="headerBackBtn" onClick={onBackClick}>
                  <img className="headerBackIcon" src={backArrow} alt="back-arrow" />
                  <span className="headerBackText">Back</span>
                </div>
              )}
              <div className="content">
                <div className="mainText">
                  <p className="katon360Classroom">Welcome to</p>
                  <h1 className="teachSmartLearnContainer">
                    <p className="teachSmart">KATON VIRTUAL</p>
                    <p className="teachSmart">SCHOOL</p>
                  </h1>
                </div>
                <p className="welcomeToThe">
                  Start your learning journey by enrolling in your preferred programme and access live classes, study materials, and assessments—all in one place.
                </p>
              </div>
              {isMobileScreen && landingImage()}
              <div className="buttonContainerVirtual">
                <button
                  className="heroCta btnRegister"
                  onClick={() =>
                    onButtonClick(
                      "/virtual-school/virtual-signup",
                      ButtonClickEvents?.STUDENT_SIGNUP_BUTTON_CLICK,
                      "User clicked Register on Virtual School Landing Page"
                    )
                  }
                >
                  <span className="joinClassroomAs">Register</span>
                </button>
                <button
                  className="heroCta btnLogIn"
                  onClick={() =>
                    onButtonClick(
                      "/virtual-school/virtual-login",
                      ButtonClickEvents?.LOGIN_BUTTON_CLICK,
                      "User clicked Log In on Virtual School Landing Page"
                    )
                  }
                >
                  <span className="joinClassroomAs">Log In</span>
                </button>
              </div>
              <SupportCard />
            </>
          ) : (
            <>
              <div className="content">
                <div className="mainText">
                  <p className="katon360Classroom">Katon 360 Classroom</p>
                  <h1 className="teachSmartLearnContainer">
                    <p className="teachSmart">{`TEACH SMART, `}</p>
                    <p className="teachSmart">LEARN EASY.</p>
                  </h1>
                </div>
                <p className="welcomeToThe">
                  Welcome to the 360 Classroom where Teachers, Students and Parents meet to continue the traditional classroom activities.
                </p>
              </div>
              {isMobileScreen && landingImage()}
              <div className="buttonContainer">
                <button
                  className={`heroCta`}
                  onClick={() =>
                    onButtonClick(
                      isMobileScreen ? loginText?.[loginAs]?.link : "/student-signup",
                      isMobileScreen
                        ? loginText?.[loginAs]?.eventName
                        : ButtonClickEvents?.JOIN_CLASSROOM_AS_STUDENT_CLICK,
                      isMobileScreen
                        ? loginText?.[loginAs]?.description
                        : "User clicked the Join Classroom as Student button"
                    )
                  }
                >
                  <span className="joinClassroomAs">
                    {`Join Classroom as ${isMobileScreen ? loginText?.[loginAs]?.label : "Student"}`}
                  </span>
                </button>
                <button
                  className="heroCta btnEnroll"
                  onClick={() =>
                    onButtonClick(
                      "/virtual-school/virtual-signup",
                      ButtonClickEvents?.STUDENT_SIGNUP_BUTTON_CLICK,
                      "User clicked Enroll in Virtual School on Landing Page"
                    )
                  }
                >
                  <span className="joinClassroomAs">Register for Vacation Classes</span>
                </button>
              <div className="loginAscontainerMobile">
                  <div className="loginAsInnerContainer">
                    <div className="loginAsBorder"></div>
                    <div className="loginAsText">Or login as</div>
                    <div className="loginAsBorder"></div>
                  </div>
                  <div className="secondryButtonContainer">
                    <button
                      className={`heroCta heroCtaInactive`}
                      onClick={() =>
                        onButtonClick(
                          loginText?.[displayButtons?.[0]]?.link,
                          loginText?.[displayButtons?.[0]]?.eventName,
                          loginText?.[displayButtons?.[0]]?.description
                        )
                      }
                    >
                      <span className="joinClassroomAs">
                        {loginText?.[displayButtons?.[0]]?.label}
                      </span>
                    </button>
                  </div>
                  {displayButtons?.[1] && (
                    <div className="secondryButtonContainer">
                      <button
                        className={`heroCta heroCtaInactive`}
                        onClick={() =>
                          onButtonClick(
                            loginText?.[displayButtons?.[1]]?.link,
                            loginText?.[displayButtons?.[1]]?.eventName,
                            loginText?.[displayButtons?.[1]]?.description
                          )
                        }
                      >
                        <span className="joinClassroomAs">
                          {loginText?.[displayButtons?.[1]]?.label}
                        </span>
                      </button>
                    </div>
                  )}
                  <div className="secondryButtonContainer otherLoginButtonContainer">
                    {displayButtons?.slice(2)?.map((value: string) => (
                      <button
                        key={value}
                        className={`heroCta heroCtaInactive`}
                        onClick={() =>
                          onButtonClick(
                            loginText?.[value]?.link,
                            loginText?.[value]?.eventName,
                            loginText?.[value]?.description
                          )
                        }
                      >
                        <span className="joinClassroomAs">
                          {loginText?.[value]?.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  </div>
                </div>
              </>
          )}
        </div>
        {!isMobileScreen && landingImage()}
      </div>
    </section>
  );
};

export default LandingPage;
